import { get } from 'svelte/store';
import { setUpI18n } from 'src/services/i18n';
import { socket_disconnect } from 'src/services/socket';

import { profiles } from 'src/stores/features/profiles';
import { toast, type ImageGroupImageType } from '@Shelly/shelly-design-system';
import { showGlobalLoader } from 'src/stores/ui/global-loader';
import { user_data, user_settings } from 'src/stores/account/user';
import { os, app } from 'src/stores/session/native-bridge';
import { initializing_language } from 'src/stores/session/preferences';
import { clearDataOnLogin, handleLoginErrors } from 'src/helpers/account_helpers';
import { fillMissingUnitNames } from 'src/helpers/utilities';
import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
import { loadingData } from 'src/stores/ui/loading-data';

import { get_user_pin, pullDataWithLoading, standardLogin } from 'src/services/http';
import { isMultiUnitResult } from 'src/services/auth_v2';
import type {
	AvailableSubUnits,
	AvailableUnits,
	HandleLoginResult,
	MultiUnitLoginResult,
	MultiUnitPending,
} from 'src/types/responses/usersAuthResponse';

import {
	account_switch_mobile_ios,
	change_lang_ios,
	logout_mobile_ios,
} from 'src/services/native_container_requests_ios';
import {
	account_switch_mobile_android,
	logout_mobile_android,
} from 'src/services/native_container_requests_android';
import { mobile } from 'src/services/mobile-rework';
import { LANG_DICT } from 'src/constants';
import { stopAndClearNotifications } from 'src/helpers/notifications/device_notifications_helper';
import { shouldForceLogout } from 'src/services/auth_errors';
import type { LangaugeDropdownItem } from 'src/types/interfaces';
import type { MenuListDataType } from './DropdownItem.types';
import { normalizeEmail } from 'src/helpers/email_helpers';
import { clearCameraStores } from 'src/components/Camera/helpers/camera-helpers';

const handleLogin = async (
	email: string,
	pass: string,
	profileId: number,
	sub: number | undefined = undefined,
	unitId?: string,
	subId?: string,
	knownAvailableUnits?: AvailableUnits,
): Promise<HandleLoginResult> => {
	email = normalizeEmail(email);

	console.log('handleLogin in profile_helpers');
	try {
		const res = await standardLogin(email, pass, unitId, subId);

		if (!res.isok) {
			let errorMessage = handleLoginErrors(res, email);
			toast.open({
				type: 'danger',
				title: errorMessage.heading,
				text: errorMessage.message,
			});

			if (shouldForceLogout(res as Parameters<typeof shouldForceLogout>[0])) {
				if (get(os) === 'IOS') {
					logout_mobile_ios(email.toLowerCase());
				} else {
					logout_mobile_android(email.toLowerCase());
				}
				profiles.remove(email);
			}
			throw new Error('Error in handle login');
		}

		if (isMultiUnitResult(res)) {
			const multiLoginResult = await handleMultiUnitlogin(
				res,
				unitId,
				sub,
				email,
				pass,
				profileId,
			);

			if (multiLoginResult) {
				return multiLoginResult as any;
			}
		}

		socket_disconnect();

		if (mobile.isMobile) {
			if (mobile.OS === 'IOS') {
				account_switch_mobile_ios(email.toLowerCase());
			} else {
				account_switch_mobile_android(email.toLowerCase());
			}
		}

		profiles.change(email);
		// Reset the load gate so the switched-in account's handleInitialLoad
		// (→ refreshHomes → get_units_info) runs; pullDataWithLoading is a no-op
		// while loadingData is true.
		loadingData.set(false);
		await pullDataWithLoading();
		get_user_pin();

		aiSceneChatStore.loadChatPreferences();

		return { isok: true };
	} catch (error) {
		console.error(error);
		return { isok: false };
	}
};

const handleMultiUnitlogin = async (
	res: MultiUnitLoginResult,
	unitId: string,
	sub: number,
	email: string,
	pass: string,
	profileId: number,
) => {
	// `available_units` / `available_sub_units` are no longer written from the
	// login response here — `get_units_info` (via `refreshHomes`, run by
	// `authLogin` during deferred selection and by `handleInitialLoad` after a
	// completed login) is the single source of truth for those fields.

	if (!unitId) {
		const ownedEntries = Object.entries(res.availableUnits ?? {});
		const sharedEntries = Object.entries(res.availableSubUnits ?? {});
		const totalChoices = ownedEntries.length + sharedEntries.length;

		if (totalChoices > 1) {
			return {
				isok: true,
				multipleUnits: true,
				availableUnits: res.availableUnits,
				availableSubUnits: res.availableSubUnits,
				// Discovery token for the target account so the picker can render
				// from its own `get_units_info` (account-scoped, no global pollution).
				discoveryToken: res.data?.token,
				discoveryApiUrl: res.data?.user_api_url,
			};
		}

		const hasTokens = res.accessToken && res.refreshToken;

		if (ownedEntries.length === 1) {
			if (hasTokens) {
				return undefined;
			}
			return handleLogin(
				email,
				pass,
				profileId,
				sub,
				ownedEntries[0][0],
				undefined,
				res.availableUnits,
			);
		}

		if (sharedEntries.length === 1) {
			if (hasTokens) {
				return undefined;
			}
			const [sharedUnitId, sharedValue] = sharedEntries[0];
			const sharedSubId = typeof sharedValue === 'object' ? String(sharedValue.sub) : undefined;
			return handleLogin(
				email,
				pass,
				profileId,
				sub,
				sharedUnitId,
				sharedSubId,
				res.availableUnits,
			);
		}
	}

	return undefined;
};

const checkProfile = (email: string) => {
	const profile = get(profiles).find((p) => p.email === email);
	if (!profile) throw new Error('Profile not found');
	return profile;
};

export const onChangeProfile = async (
	email: string,
	id: number,
	sub: number | undefined = undefined,
	unitId?: string,
	subId?: string,
	knownAvailableUnits?: AvailableUnits,
): Promise<MultiUnitPending | void> => {
	showGlobalLoader.set(true);
	try {
		const { pass } = checkProfile(email);
		if (!email || !pass) throw new Error('Profile not found');
		stopAndClearNotifications();
		clearCameraStores();
		clearDataOnLogin();
		const res = await handleLogin(email, pass, id, sub, unitId, subId, knownAvailableUnits);
		if (!res.isok) throw new Error('Error in handle login');
		if (res.multipleUnits) {
			return {
				multipleUnits: true,
				availableUnits: res.availableUnits,
				availableSubUnits: res.availableSubUnits,
				discoveryToken: res.discoveryToken,
				discoveryApiUrl: res.discoveryApiUrl,
			};
		}
		profiles.change(email);
	} catch (error) {
		console.error(error);
	} finally {
		showGlobalLoader.set(false);
	}
};

const getFirstHomeSelection = (
	availableUnits: AvailableUnits,
	availableSubUnits: AvailableSubUnits = {},
): { unitId: string; subId?: string } | undefined => {
	const ownedHomeId = Object.keys(availableUnits ?? {})[0];
	if (ownedHomeId) {
		return { unitId: ownedHomeId };
	}

	const [unitId, subUnit] = Object.entries(availableSubUnits ?? {})[0] ?? [];
	if (!unitId) {
		return undefined;
	}

	return {
		unitId,
		subId: typeof subUnit === 'object' ? String(subUnit.sub) : undefined,
	};
};

/**
 * Used by revoked-home recovery when the current account has no usable home.
 * Reuses the normal profile-switch flow, but auto-selects the first available
 * home instead of asking the user to choose.
 */
export const switchToAnySavedProfile = async (excludedEmail?: string): Promise<boolean> => {
	const candidateProfiles = get(profiles).filter(
		(profile): profile is MenuListDataType & { email: string; pass: string; id: number } =>
			!!profile.email &&
			!!profile.pass &&
			normalizeEmail(profile.email) !== normalizeEmail(excludedEmail ?? ''),
	);

	for (const profile of candidateProfiles) {
		try {
			const result = await onChangeProfile(profile.email, profile.id, profile.sub);

			if (result && result.multipleUnits) {
				const selection = getFirstHomeSelection(result.availableUnits, result.availableSubUnits);
				if (!selection) {
					continue;
				}

				await onChangeProfile(
					profile.email,
					profile.id,
					profile.sub,
					selection.unitId,
					selection.subId,
					result.availableUnits,
				);
			}

			if (normalizeEmail(get(user_data).email) === normalizeEmail(profile.email)) {
				profiles.remove(excludedEmail);
				return true;
			}
		} catch (error) {
			console.error(
				'[profiles] Failed to switch saved profile during revoked-home recovery',
				error,
			);
		}
	}

	return false;
};

export let imgGroupHousesArr: ImageGroupImageType[] = [...Array(10).keys()].map((_, i) => {
	return {
		image: `./images/home_def/app_bg_${i + 1 < 10 ? `0${i + 1}` : `${i + 1}`}_thumb.png`,
		id: i,
		selected: false,
		type: 'house',
	};
});

export const checkIfAvatarIsColor = (avatar) => avatar?.includes('color-avatar-') ?? false;

export const getProfileAvatarImageSrc = (
	activeProfile: MenuListDataType | undefined,
): string | undefined => {
	const raw = activeProfile?.acc_avatar?.image;
	if (typeof raw !== 'string') return;

	const avatar = raw.replace('thumb_', '');
	if (!avatar) return;
	if (checkIfAvatarIsColor(avatar)) return '';
	return avatar;
};

/**
 * Drop a logged-in profile from local storage after its cloud account was removed
 * (e.g. convert single-home account → home on another profile). Does not switch
 * sessions — the active profile stays logged in.
 */
export const purgeLocalLoggedInProfile = (email: string): void => {
	const normalized = normalizeEmail(email);
	const match = get(profiles).find((p) => normalizeEmail(p.email) === normalized);
	if (!match) return;

	if (get(app) && mobile.isMobile) {
		if (mobile.OS === 'IOS') {
			logout_mobile_ios(normalized);
		} else {
			logout_mobile_android(normalized);
		}
	}

	profiles.remove(match.email);
};

export const changeLanguage = async (dropdownItem: Partial<LangaugeDropdownItem>) => {
	let newLang = Object.keys(LANG_DICT).filter((e) => e === dropdownItem.language)[0];

	if (get(user_settings).lang !== newLang) {
		const activeLang = await setUpI18n(newLang);
		initializing_language.set(activeLang);
		user_settings.set({ ...get(user_settings), lang: activeLang });
		if (get(app)) {
			if (get(os) === 'IOS') {
				change_lang_ios(activeLang);
			} else {
				// login_mobile_android(get(user_data).email, get(user_data).user_api_url, lang);
			}
		}
	}
};
